import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useUsers } from '../../users/hooks/useUsers';
import UsersTable from '../../users/components/UsersTable';
import UserDetailModal from '../../users/components/UserDetailModal';
import CheckinModal from './CheckinModal';
import useSpaces from '../../gatherings/hooks/data/useSpaces';
import DailyGatheringsOverview from './DailyGatheringsOverview';
import GatheringsCalendar from './GatheringsCalendar';
import CheckedInUsersPanel from './CheckedInUsersPanel';

const SpaceOpsPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [clearSearchTrigger, setClearSearchTrigger] = useState<number>(0);

  // Get spaces data
  const { spaces, isLoading: spacesLoading, error: spacesError } = useSpaces(user?.token || null);

  // State for membership filter
  const [membershipFilter, setMembershipFilter] = useState<'all' | 'members'>('all');
  
  // Get users data from our custom hook with pagination
  const { 
    users, 
    allUsers,
    isLoading, 
    isLoadingAllUsers, 
    error, 
    refresh,
    currentPage,
    totalPages,
    goToPage,
    setFilter
  } = useUsers(user?.token || null, undefined, undefined, {
    includeUsageData: true,
    membersOnly: membershipFilter === 'members'
  });

  // State for space selection
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>('');
  const selectedSpace = spaces.find(space => space.id === selectedSpaceId);
  const selectedSpaceName = selectedSpace?.name || 'All Spaces';
  const spaceIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    spaces.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [spaces]);
  const usersReady = !isLoadingAllUsers && allUsers.length > 0;

  // Handle space selection change
  const handleSpaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSpaceId(e.target.value);
  };

  // Handle membership filter change
  const handleMembershipFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilter = e.target.value as 'all' | 'members';
    setMembershipFilter(newFilter);
    
    // Use server-side filtering instead of client-side
    const membersOnly = newFilter === 'members';
    setFilter(undefined, undefined, membersOnly);
  };

  // No need for client-side filtering anymore - server handles it
  const filteredUsers = users;

  // Handle view user details
  const handleViewUserDetails = (userId: string) => {
    setSelectedUserId(userId);
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
    // Trigger search clear in UsersTable
    setClearSearchTrigger(prev => prev + 1);
  };

  // Note: Users are NOT filtered by space - they can check in to any location
  // Space filtering only applies to gatherings/events

  return (
    <div className="space-ops-container">
      <div className="space-ops-content">
        <div className="space-ops-header">
          <div className="header-title-section">
            <h1>Space Operations Dashboard</h1>
            <p className="space-ops-subtitle">
              Admin dashboard for managing gatherings, calendar, and user check-ins
            </p>
          </div>
          
          <div className="space-selector-section">
            <div className="space-filter">
              <label htmlFor="space-select">Filter by Space:</label>
              {spacesLoading ? (
                <span className="loading-indicator">Loading spaces...</span>
              ) : spacesError ? (
                <span className="error-indicator">Error loading spaces</span>
              ) : (
                <select
                  id="space-select"
                  value={selectedSpaceId}
                  onChange={handleSpaceChange}
                  className="space-select"
                >
                  <option value="">All Spaces</option>
                  {spaces.map((space) => (
                    <option key={space.id} value={space.id}>
                      {space.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="space-ops-grid">
          {/* Daily Gatherings - Top Left */}
          <div className="space-ops-section daily-gatherings">
            <h2>
              Today's Gatherings
              {selectedSpace && ` - ${selectedSpace.name}`}
            </h2>
            <DailyGatheringsOverview
              token={user?.token || null}
              selectedSpaceId={selectedSpaceId}
            />
          </div>

          {/* Gatherings Calendar - Top Right */}
          <div className="space-ops-section gatherings-calendar">
            <h2>
              Gatherings Calendar
              {selectedSpace && ` - ${selectedSpace.name}`}
            </h2>
            <GatheringsCalendar
              token={user?.token || null}
              selectedSpaceId={selectedSpaceId}
            />
          </div>

          {/* User Management - Bottom Left */}
          <div className="space-ops-section user-search">
            <div className="user-section-header">
              <h2>All Users</h2>
              <div className="user-controls">
                <div className="membership-filter">
                  <label htmlFor="membership-filter">Filter:</label>
                  <select
                    id="membership-filter"
                    value={membershipFilter}
                    onChange={handleMembershipFilterChange}
                    className="membership-select"
                    disabled={isLoadingAllUsers}
                  >
                    <option value="all">All Users</option>
                    <option value="members">Members Only</option>
                  </select>
                  {isLoadingAllUsers && (
                    <span className="loading-text">Loading all users...</span>
                  )}
                </div>
              </div>
            </div>
            <UsersTable
              users={filteredUsers}
              allUsers={allUsers}
              isLoading={isLoading}
              isLoadingAllUsers={isLoadingAllUsers}
              error={error}
              refresh={refresh}
              onViewDetails={handleViewUserDetails}
              clearSearchTrigger={clearSearchTrigger}
            />
            
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="prev-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="next-button"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* In Space - Bottom Right */}
          <div className="space-ops-section in-space">
            <h2>
              Users in Space
              {selectedSpace && ` - ${selectedSpace.name}`}
            </h2>
            <CheckedInUsersPanel
              token={user?.token || null}
              selectedSpaceId={selectedSpaceId}
              allUsers={allUsers}
              selectedSpaceName={selectedSpaceName}
              spaceIdToName={spaceIdToName}
              usersReady={usersReady}
              onUserClick={handleViewUserDetails}
            />
          </div>
        </div>
      </div>

      <UserDetailModal
        open={selectedUserId !== null}
        onClose={handleCloseModal}
        userId={selectedUserId}
      />
    </div>
  );
};

export default SpaceOpsPage;
